import { signInWithRedirect } from 'aws-amplify/auth'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Button } from '../Button'
import styles from './one-tap-variant.module.scss'
import { constants } from '@/lib/constants'
import { useAdminMode } from '@/lib/hooks/useAdminMode'
import { useCookieBot } from '@/lib/hooks/useCookiebot'
import { ROUTES } from '@/lib/routes'
import { getWithExpiry, setWithExpiry, storageAvailable } from '@/lib/utilities/system'
import { addToDataLayer } from '@/lib/utilities/vehicle'
import { postAnalyticsEvent } from '@/store/actions/event'
import { storeRedirectUrl, storeUsedSocialLogin } from '@/store/reducers/user'
import { selectIsAuthenticated } from '@/store/selectors/user'

export const OneTapVariant = () => {
  const dispatch = useDispatch()
  const router = useRouter()

  const isAdminMode = useAdminMode()
  const cookiebot = useCookieBot()
  const isAuthenticated = useSelector(selectIsAuthenticated)

  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const routerAsPathNoPrams = router.asPath.split('?')[0]
    const isOnLandingPage = [
      ROUTES.enquiryLandingPage.regex,
      ROUTES.enquiryLandingAltPage.regex,
    ].some(regex => regex.test(routerAsPathNoPrams))
    let storageKeyExists = false

    if (storageAvailable('localStorage')) {
      const storageKey = getWithExpiry(constants.localStorageKey.oneTap)
      storageKeyExists = !!storageKey
    }

    const shouldHideOneTap = isOnLandingPage || storageKeyExists || cookiebot.isVisible

    if (router.isReady) {
      if (shouldHideOneTap && isVisible) {
        setIsVisible(false)
      }
      if (!shouldHideOneTap && !isVisible) {
        const showTimer = setTimeout(() => {
          setIsVisible(true)
          addToDataLayer({
            event: 'one-tap-view',
          })
          dispatch(
            postAnalyticsEvent({
              event: constants.eventTypes.oneTapComponentView,
            }),
          )
        }, 1500)
        return () => {
          clearTimeout(showTimer)
        }
      }
    }
  }, [dispatch, router, isVisible, cookiebot.isVisible])

  const onCloseOneTap = () => {
    if (storageAvailable('localStorage')) {
      const oneDay = 86400000
      setWithExpiry(constants.localStorageKey.oneTap, true, oneDay)
    }
    addToDataLayer({
      event: `social-one-tap`,
      label: 'close',
    })
    dispatch(
      postAnalyticsEvent({
        event: constants.eventTypes.oneTapComponentAction,
        payload: {
          label: 'close',
        },
      }),
    )
    setIsVisible(false)
  }

  const onLogIn = () => {
    addToDataLayer({
      event: `social-one-tap`,
      label: 'google',
    })
    dispatch(
      postAnalyticsEvent({
        event: constants.eventTypes.oneTapComponentAction,
        payload: {
          label: 'google',
        },
      }),
    )
    dispatch(storeRedirectUrl(router.asPath))
    dispatch(storeUsedSocialLogin(true))
    signInWithRedirect({ provider: 'Google' })
  }

  if (isAuthenticated || !isVisible || isAdminMode) {
    return null
  }

  return (
    <div className={`${styles.container} one-tap-component`}>
      <div className={styles.header}>
        <div className={styles.label}>
          <Image
            src={`${constants.imageHost.assets}/general/google.svg`}
            alt="Google Logo"
            height={20}
            width={20}
          />
          <p>Sign in with Google</p>
        </div>
        <Button
          hierarchy="borderless-gray"
          size="md"
          icon="X"
          onClick={onCloseOneTap}
        />
      </div>
      <div className={styles.cta}>
        <p className={styles.leading}>Use your Google account to sign in to Leaseloco.com</p>
        <p className={styles.sub}>
          No more passwords to remember. <br />
          Signing in is fast, simple and secure.
        </p>
        <Button
          hierarchy="filled"
          size="lg"
          label="Continue"
          onClick={() => onLogIn()}
        />
      </div>
    </div>
  )
}
